import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, PhoneOff, UserPlus, Video } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { formatDateTime } from "@/utils/formatDate";
import { InviteModal } from "../components/InviteModal";
import { MeetingNotesThread } from "../components/MeetingNotesThread";
import { OccurrenceStatusBadge } from "../components/OccurrenceStatusBadge";
import {
  useOccurrence,
  useOccurrenceAction,
  useRespond,
  useUpdateMeeting,
} from "../hooks/useMeetings";

const RSVP_LABELS: Record<string, string> = {
  invited: "zaproszono",
  accepted: "potwierdzono",
  declined: "odmówiono",
};

export function OccurrenceDetailPage() {
  const { occurrenceId } = useParams<{ occurrenceId: string }>();
  const navigate = useNavigate();
  const { data: occ, isLoading } = useOccurrence(occurrenceId);
  const respond = useRespond(occ?.meeting_id ?? "");
  const cancelAction = useOccurrenceAction("cancel");
  const endAction = useOccurrenceAction("end");
  const updateMeeting = useUpdateMeeting(occ?.meeting_id ?? "");
  const [rsvpBusy, setRsvpBusy] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");

  if (isLoading || !occ) {
    return (
      <div className="flex justify-center p-10">
        <Spinner />
      </div>
    );
  }

  const startsAt = new Date(occ.starts_at);
  const startsLabel = `${startsAt.toLocaleDateString("pl-PL", { weekday: "long" })}, ${formatDateTime(startsAt)}`;

  const rsvp = async (answer: "accepted" | "declined") => {
    setRsvpBusy(true);
    try {
      await respond.mutateAsync(answer);
    } finally {
      setRsvpBusy(false);
    }
  };

  const startEditTitle = () => {
    if (!occ.is_organizer) return;
    setTitleDraft(occ.title);
    setEditingTitle(true);
  };

  const saveTitle = async () => {
    const next = titleDraft.trim();
    setEditingTitle(false);
    if (!next || next === occ.title) return;
    await updateMeeting.mutateAsync({ title: next });
  };

  return (
    <div className="mx-auto max-w-4xl space-y-5 p-4">
      <button
        type="button"
        onClick={() => navigate("/meetings")}
        className="flex items-center gap-1 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
      >
        <ArrowLeft size={14} /> Spotkania
      </button>

      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {editingTitle ? (
                <input
                  type="text"
                  autoFocus
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onBlur={() => void saveTitle()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void saveTitle();
                    if (e.key === "Escape") setEditingTitle(false);
                  }}
                  className="min-w-0 flex-1 rounded-md border border-[var(--color-primary)] bg-[var(--color-bg-surface)] px-2 py-1 text-lg font-bold text-[var(--color-text-primary)] focus:outline-none"
                />
              ) : (
                <>
                  <h1
                    className={`truncate text-lg font-bold text-[var(--color-text-primary)] ${
                      occ.is_organizer ? "cursor-text" : ""
                    }`}
                    title={
                      occ.is_organizer
                        ? "Kliknij dwukrotnie, aby zmienić nazwę"
                        : undefined
                    }
                    onDoubleClick={startEditTitle}
                  >
                    {occ.title}
                  </h1>
                  {occ.is_organizer && (
                    <button
                      type="button"
                      title="Zmień nazwę"
                      onClick={startEditTitle}
                      className="shrink-0 rounded p-1 text-[var(--color-text-muted)] hover:bg-black/5 hover:text-[var(--color-text-primary)] dark:hover:bg-white/10"
                    >
                      <Pencil size={14} />
                    </button>
                  )}
                </>
              )}
              <OccurrenceStatusBadge status={occ.status} />
            </div>
            <p className="mt-1 text-sm capitalize text-[var(--color-text-secondary)]">
              {startsLabel} · {occ.duration_minutes} min
              {occ.organizer ? ` · organizator: ${occ.organizer.name}` : ""}
            </p>
            {occ.ze_project_name && (
              <p className="text-sm text-[var(--color-text-secondary)]">
                Projekt ZE: <b>{occ.ze_project_name}</b>
              </p>
            )}
            {occ.description && (
              <p className="mt-2 whitespace-pre-wrap text-sm text-[var(--color-text-primary)]">
                {occ.description}
              </p>
            )}
          </div>

          <div className="flex shrink-0 flex-col items-end gap-2">
            {occ.can_join && (
              <button
                type="button"
                onClick={() => navigate(`/meetings/room/${occ.id}`)}
                className="flex items-center gap-2 rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
              >
                <Video size={16} />{" "}
                {occ.is_organizer && occ.status === "scheduled"
                  ? "Rozpocznij"
                  : "Dołącz"}
              </button>
            )}
            {occ.status === "scheduled" && (
              <div className="flex gap-1.5">
                <button
                  type="button"
                  disabled={rsvpBusy || occ.my_rsvp === "accepted"}
                  onClick={() => void rsvp("accepted")}
                  className={`rounded-md border px-2.5 py-1 text-xs ${
                    occ.my_rsvp === "accepted"
                      ? "border-emerald-500 text-emerald-600"
                      : "border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-emerald-500"
                  }`}
                >
                  Będę
                </button>
                <button
                  type="button"
                  disabled={rsvpBusy || occ.my_rsvp === "declined"}
                  onClick={() => void rsvp("declined")}
                  className={`rounded-md border px-2.5 py-1 text-xs ${
                    occ.my_rsvp === "declined"
                      ? "border-red-500 text-red-500"
                      : "border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-red-500"
                  }`}
                >
                  Nie będę
                </button>
              </div>
            )}
            {occ.is_organizer && (
              <button
                type="button"
                onClick={() => setInviteOpen(true)}
                className="flex items-center gap-1.5 rounded-md border border-[var(--color-border)] px-3 py-1.5 text-xs text-[var(--color-text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
              >
                <UserPlus size={13} /> Zaproś
              </button>
            )}
            {occ.is_organizer && occ.status === "live" && (
              <button
                type="button"
                onClick={() => void endAction.mutateAsync(occ.id)}
                className="flex items-center gap-1.5 rounded-md border border-red-500 px-3 py-1.5 text-xs font-medium text-red-500 hover:bg-red-500 hover:text-white"
              >
                <PhoneOff size={13} /> Zakończ spotkanie
              </button>
            )}
            {occ.is_organizer && occ.status === "scheduled" && (
              <button
                type="button"
                onClick={() => void cancelAction.mutateAsync(occ.id)}
                className="text-xs text-red-500 hover:underline"
              >
                Odwołaj to wystąpienie
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-4">
          <h3 className="mb-2 text-sm font-semibold text-[var(--color-text-primary)]">
            Uczestnicy ({occ.participants.length})
          </h3>
          <ul className="space-y-1.5">
            {occ.participants.map((p) => (
              <li key={p.id} className="flex items-center gap-2 text-sm">
                <UserAvatar
                  userId={p.user.id}
                  fullName={p.user.name}
                  size="xs"
                />
                <span className="text-[var(--color-text-primary)]">
                  {p.user.name}
                </span>
                {p.role === "organizer" && (
                  <span className="rounded bg-black/5 px-1.5 py-0.5 text-[10px] uppercase text-[var(--color-text-muted)] dark:bg-white/10">
                    organizator
                  </span>
                )}
                {!p.user.is_internal && (
                  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                    podwykonawca
                  </span>
                )}
                <span className="ml-auto text-xs text-[var(--color-text-muted)]">
                  {RSVP_LABELS[p.rsvp] ?? p.rsvp}
                </span>
              </li>
            ))}
          </ul>

          {occ.attendance.length > 0 && (
            <>
              <h3 className="mb-1 mt-4 text-sm font-semibold text-[var(--color-text-primary)]">
                Obecność
              </h3>
              <ul className="space-y-0.5 text-xs text-[var(--color-text-secondary)]">
                {occ.attendance.map((a, idx) => (
                  <li key={`${a.peer_id}-${idx}`}>
                    {a.display_name}
                    {a.is_guest && " (gość)"} —{" "}
                    {new Date(a.joined_at).toLocaleTimeString("pl-PL", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                    {a.left_at
                      ? `–${new Date(a.left_at).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })}`
                      : " (obecny)"}
                  </li>
                ))}
              </ul>
            </>
          )}

          {occ.is_organizer && (
            <div className="mt-4 border-t border-[var(--color-border)] pt-4">
              <button
                type="button"
                onClick={() => setInviteOpen(true)}
                className="flex items-center gap-1.5 rounded-md bg-[var(--color-primary)] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
              >
                <UserPlus size={14} /> Zaproś użytkownika ELGW5 lub gościa
              </button>
            </div>
          )}
        </div>

        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-4">
          <h3 className="mb-2 text-sm font-semibold text-[var(--color-text-primary)]">
            Notatki
          </h3>
          <MeetingNotesThread
            occurrenceId={occ.id}
            occurrenceStatus={occ.status}
            isOrganizer={occ.is_organizer}
            meetingId={occ.meeting_id}
            guestNotesEnabled={occ.guests_can_see_notes}
          />
        </div>
      </div>

      {occ.is_organizer && (
        <InviteModal
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          meetingId={occ.meeting_id}
          occurrenceId={occ.id}
        />
      )}
    </div>
  );
}
